"use client";

import { SafeHydrate } from "@/SafeHydrate";
import { motion, useReducedMotion } from "framer-motion";
import { Calculator, Calendar, Flame, Utensils, Scale, ShieldAlert, Sparkles, Compass, FileSpreadsheet, Cat, Dog, Activity } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
interface ToolItem {
  id: string;
  name: string;
  description: string;
  category: string;
  iconName: string;
}
const toolsList: ToolItem[] = [{
  id: "dog-age",
  name: "Dog Age Calculator",
  description: "Translate canine years into human biological equivalents based on breed size.",
  category: "Milestones",
  iconName: "dog"
}, {
  id: "cat-age",
  name: "Cat Age Calculator",
  description: "Map feline life stages from kittenhood through senior years with veterinary guidelines.",
  category: "Milestones",
  iconName: "cat"
}, {
  id: "pet-calorie",
  name: "Pet Calorie Calculator",
  description: "Determine daily resting energy requirements according to activity level and neuter status.",
  category: "Nutrition",
  iconName: "flame"
}, {
  id: "dog-food",
  name: "Dog Food Calculator",
  description: "Calculate accurate cup and gram portion sizes tailored to your dog's caloric profile.",
  category: "Feeding",
  iconName: "utensils"
}, {
  id: "cat-food",
  name: "Cat Food Calculator",
  description: "Balance wet and dry feeding ratios to meet daily hydration and protein targets.",
  category: "Feeding",
  iconName: "calculator"
}, {
  id: "puppy-weight",
  name: "Puppy Weight Predictor",
  description: "Estimate adult weight trajectory using current age and breed growth curves.",
  category: "Development",
  iconName: "scale"
}, {
  id: "healthy-weight",
  name: "Healthy Weight Calculator",
  description: "Assess body condition scores and ideal target weight ranges for dogs and cats.",
  category: "Health",
  iconName: "activity"
}, {
  id: "chocolate-toxicity",
  name: "Chocolate Toxicity Calculator",
  description: "Quickly assess theobromine toxicity risk levels based on pet weight and chocolate type.",
  category: "Emergency & Safety",
  iconName: "alert"
}, {
  id: "breed-selector",
  name: "Breed Selector",
  description: "Find compatible dog and cat breeds matched to your living space, time, and lifestyle.",
  category: "Adoption",
  iconName: "compass"
}, {
  id: "insurance-estimator",
  name: "Pet Insurance Cost Estimator",
  description: "Compare baseline coverage tiers, deductibles, and average monthly premium ranges.",
  category: "Planning",
  iconName: "spreadsheet"
}, {
  id: "dog-name-gen",
  name: "Dog Name Generator",
  description: "Curated, classic, and personality-inspired canine names organized by temperament.",
  category: "Lifestyle",
  iconName: "sparkles"
}, {
  id: "cat-name-gen",
  name: "Cat Name Generator",
  description: "Distinctive feline names inspired by coat patterns, literary figures, and mythology.",
  category: "Lifestyle",
  iconName: "sparkles"
}];
function getToolIcon(iconName: string) {
  switch (iconName) {
    case "dog":
      return <Dog className="size-5 text-foreground" />;
    case "cat":
      return <Cat className="size-5 text-foreground" />;
    case "flame":
      return <Flame className="size-5 text-foreground" />;
    case "utensils":
      return <Utensils className="size-5 text-foreground" />;
    case "calculator":
      return <Calculator className="size-5 text-foreground" />;
    case "scale":
      return <Scale className="size-5 text-foreground" />;
    case "activity":
      return <Activity className="size-5 text-foreground" />;
    case "alert":
      return <ShieldAlert className="size-5 text-foreground" />;
    case "compass":
      return <Compass className="size-5 text-foreground" />;
    case "spreadsheet":
      return <FileSpreadsheet className="size-5 text-foreground" />;
    case "calendar":
      return <SafeHydrate><Calendar className="size-5 text-foreground" /></SafeHydrate>;
    case "sparkles":
    default:
      return <Sparkles className="size-5 text-foreground" />;
  }
}
export default function Tools() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.05
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 16
    },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.4,
        ease: [0.16, 1, 0.3, 1]
      }
    }
  };
  return <section id="helpful-tools" data-nav="light" className="bg-background py-16 md:py-24 border-t border-border">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        {/* Section Header */}
        <div className="max-w-3xl mb-12 md:mb-16">
          <div className="flex items-center gap-2 mb-3">
            <span className="h-px w-6 bg-border" />
            <span className="text-xs font-bold tracking-widest text-muted-foreground uppercase">
              Calculators & Tools
            </span>
          </div>

          <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight text-foreground leading-tight">
            Interactive pet health and care calculators
          </h2>

          <p className="mt-4 text-base sm:text-lg text-muted-foreground leading-relaxed">
            Estimate nutrition needs, assess safety risks, and track development milestones with veterinary-modeled calculation tools.
          </p>
        </div>

        {/* Tools Grid */}
        <motion.div variants={containerVariants} initial="hidden" whileInView="show" viewport={{
        once: true,
        amount: 0.1
      }} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 sm:gap-6">
          {toolsList.map((tool, i) => <motion.div key={tool.id} data-index={i} variants={itemVariants} className="h-full">
              <Card className="h-full border border-border bg-card/60 hover:bg-card transition-[background-color,border-color,box-shadow] duration-200 hover:border-border/80 hover:shadow-sm rounded-xl p-0 flex flex-col justify-between overflow-hidden">
                <div>
                  <CardHeader className="p-6 pb-4">
                    <div className="flex items-center justify-between gap-3 mb-4">
                      <div className="size-10 rounded-lg bg-secondary/80 border border-border flex items-center justify-center">
                        {getToolIcon(tool.iconName)}
                      </div>
                      <Badge variant="secondary" className="bg-muted text-muted-foreground text-xs font-medium px-2.5 py-0.5 rounded-md border border-border">
                        Coming Soon
                      </Badge>
                    </div>

                    <div className="text-xs font-semibold text-muted-foreground tracking-wide uppercase mb-1">
                      {tool.category}
                    </div>

                    <CardTitle className="font-serif text-lg font-semibold text-card-foreground leading-snug">
                      {tool.name}
                    </CardTitle>
                  </CardHeader>

                  <CardContent className="px-6 pb-6 pt-0">
                    <CardDescription className="text-sm text-muted-foreground leading-relaxed">
                      {tool.description}
                    </CardDescription>
                  </CardContent>
                </div>

                <div className="px-6 py-3.5 bg-muted/40 border-t border-border flex items-center justify-between text-xs text-muted-foreground">
                  <span>Clinical algorithm model</span>
                  <span className="font-medium text-foreground/80">In development</span>
                </div>
              </Card>
            </motion.div>)}
        </motion.div>

        {/* Clinical Governance Callout */}
        <div className="mt-12 md:mt-16 p-6 rounded-lg bg-secondary/40 border border-border flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div className="space-y-1">
            <h4 className="font-sans text-sm font-semibold text-foreground">
              Clinical standard methodology
            </h4>
            <p className="text-xs sm:text-sm text-muted-foreground">
              All formulas follow published guidelines from the American Animal Hospital Association (AAHA) and WSAVA Nutritional Assessment protocols.
            </p>
          </div>
          <Badge variant="outline" className="border-border text-foreground font-medium text-xs whitespace-nowrap bg-background">
            Veterinary Reviewed
          </Badge>
        </div>
      </div>
    </section>;
}